import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    WeezHeader,
    Button,
    Icon,
    Card,
    ContentCard,
    Tooltip,
    FilterSetController,
    Filter,
    SelectButton,
    Dropdown,
    Table,
    Paginator,
    TableCellDropdown,
    ButtonDropdown,
    Status,
    ToastContext,
    Message
} from '@weezevent/nacre';
import DropdownPageSize from '../../manager/components/dropdownPageSize';
import WaitingListTicketModel from '../../../models/waitingListTicketModel';
import WaitingListModel from '../../../models/waitingListModel';
import { useFilters, handleSearchQuery } from '../../manager/components/context';
import LoadingTable from '../../../components/loadingTable';
import SortDropdown from '../../../components/sortDropdown';
import Style from './list.module.css';
import { AttendeeDate } from '../../attendee/list/components/attendee-row/attendee-row';
import { Loader } from 'semantic-ui-react';
import ConfirmModal from '../../../components/confirmModal';
import { useNotifications, Notification } from '../../../components/notifications/notificationsProvider';
import SubscriptionModal from './components/subscriptionModal';

const statusOptions = {
    pending: 'blue',
    used: 'green',
    ongoing: 'orange',
    expired: 'gray',
    waiting_confirmation: 'purple',
    canceled: 'red'
};

function HeaderRow({ active = false, ordering, onChangeSort = () => {}, waitingListOrdering }) {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader active={active} className={Style['tr-header']}>
            <SortDropdown
                className={Style['width-x-regular']}
                name="created"
                ordering={ordering}
                label={t('ticket.waiting_list.subscribers.list.tableHeader.date')}
                items={[
                    <Dropdown.Item.Icons key={1} item={t('ticket.common.ordering.descending')} checked={ordering === '-created'} onClick={() => onChangeSort('created')} />,
                    <Dropdown.Item.Icons key={2} item={t('ticket.common.ordering.ascending')} checked={ordering === 'created'} onClick={() => onChangeSort('-created')} />
                ]}
            />
            <th className={`${Style['width-x-large']}`}>{t('ticket.waiting_list.subscribers.list.tableHeader.subscriber')}</th>
            {waitingListOrdering === 'sorted' && (
                <SortDropdown
                    className={Style['width-x-mini']}
                    name="ordering"
                    ordering={ordering}
                    label={t('ticket.waiting_list.subscribers.list.tableHeader.position')}
                    items={[
                        <Dropdown.Item.Icons key={1} item={t('ticket.common.ordering.descending')} checked={ordering === 'ordering'} onClick={() => onChangeSort('ordering')} />,
                        <Dropdown.Item.Icons key={2} item={t('ticket.common.ordering.ascending')} checked={ordering === '-ordering'} onClick={() => onChangeSort('-ordering')} />
                    ]}
                />
            )}
            <th className={`${Style['width-x-mini']}`}>{t('ticket.waiting_list.subscribers.list.tableHeader.quantity')}</th>
            <th className={`${Style['width-x-regular']}`}>{t('ticket.waiting_list.subscribers.list.tableHeader.status')}</th>
            <th className={`${Style['width-x-mini']}`}>{t('ticket.waiting_list.subscribers.list.tableHeader.actions')}</th>
        </Table.Tr>
    );
}

function SubscriberRow({ subscriber, onSelected = () => {}, isChecked = false, waitingListOrdering, setSubscriptionsToCancel = () => {}, setSubscriptionModal = () => {} }) {
    const history = useHistory();
    const { t } = useTranslation();

    const status = React.useMemo(() => {
        return { color: statusOptions[subscriber.status], label: t(`ticket.waiting_list.subscribers.list.status.${subscriber.status}`) };
    }, [subscriber]);

    return (
        <Table.Tr
            active={isChecked}
            onSelected={(event, data) => onSelected({ event, data }, subscriber)}
            onClick={() => history.push(`waiting_lists/subscribers/${subscriber.id}`)}
        >
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`}>
                <AttendeeDate date={subscriber.created} />
            </Table.Td>
            <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>{subscriber.email}</Table.Td>
            {waitingListOrdering === 'sorted' && <Table.Td className={`${Style['width-x-mini']} ${Style['list__td-info-text']}`}>{subscriber.ordering}</Table.Td>}
            <Table.Td className={`${Style['width-x-mini']} ${Style['list__td-info-text']}`}>{subscriber.quantity}</Table.Td>
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`}>
                <Tooltip title={t(`ticket.waiting_list.subscribers.list.status.${subscriber.status}_tooltip`)} triggerOnHover={true} margin={false}>
                    <Status color={status.color} responsive text={status.label} />
                </Tooltip>
            </Table.Td>
            <TableCellDropdown renderWhenClosed className={`${Style['width-x-mini']} ${Style['inventories-actions']}`}>
                <Dropdown.Item
                    disabled={['ongoing', 'used', 'canceled', 'expired'].includes(subscriber.status)}
                    item={t('ticket.waiting_list.subscribers.list.edit_subscription')}
                    onClick={() => setSubscriptionModal({ open: false, waitingListTicket: subscriber })}
                />
                {!['canceled', 'ongoing', 'used'].includes(subscriber.status) && (
                    <>
                        <Dropdown.Line />
                        <Dropdown.Item item={t('ticket.waiting_list.subscribers.list.cancel_subscription.title')} onClick={() => setSubscriptionsToCancel([subscriber])} />
                    </>
                )}
            </TableCellDropdown>
        </Table.Tr>
    );
}

const spec = {
    multiple: ['status_in'],
    fields: ['search'],
    aliases: {}
};

const initialFilters = {
    status_in: new Set(['pending', 'ongoing', 'waiting_confirmation'])
};

export default function WaitingListSubscribersList({ organization, event, waitingListId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const { addNotification } = useNotifications();

    const [selectedLists, setSelectedLists] = React.useState({});
    const [ordering, setOrdering] = React.useState('-ordering');
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(50);
    const [allSelected, setAllSelected] = React.useState(false);
    const [subscriptionModal, setSubscriptionModal] = React.useState({
        open: false,
        waitingListTicket: null
    });
    const [filters, setFilters] = useFilters(spec, initialFilters);
    const [subscriptionsToCancel, setSubscriptionsToCancel] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [waitingListTickets, loadingWaitingListTickets, syncWaitingListTickets] = WaitingListTicketModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            waitingListId: waitingListId,
            cache: false,
            allow_cache: false,
            query: {
                ordering,
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters, false)
            }
        },
        [organization, event, waitingListId, filters, ordering, pageSize, currentPage]
    );

    const [waitingList, loadingWaitingList] = WaitingListModel.useApiModel(
        {
            organizationId: organization.id,
            id: waitingListId,
            launch: Boolean(waitingListId),
            cache: false,
            allow_cache: false
        },
        [organization, waitingListId]
    );

    const _statusOptions = React.useMemo(() => {
        return Object.keys(statusOptions).map(status => {
            return {
                key: status,
                text: (
                    <Tooltip triggerOnHover title={t(`ticket.waiting_list.subscribers.list.status.${status}_tooltip`)}>
                        {t(`ticket.waiting_list.subscribers.list.status.${status}`)}
                    </Tooltip>
                ),
                name: status
            };
        });
    }, []);

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedLists({});
    });

    const countSubscribers = React.useMemo(() => {
        return Number.parseInt(waitingListTickets?.__meta?.headers.get('total-count')) || 0;
    }, [waitingListTickets]);

    const onChangeSort = ordering => {
        setCurrentPage(0);
        setOrdering(ordering);
    };

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, subscriber) => {
            event.stopPropagation();
            setSelectedLists(currentState => {
                const newSelected = { ...currentState };
                const isSelected = subscriber.id in currentState;
                if (!checked && isSelected) {
                    delete newSelected[subscriber.id];
                }

                if (checked && !isSelected) {
                    newSelected[subscriber.id] = subscriber;
                }
                setAllSelected(Object.values(newSelected).length === waitingListTickets.length);
                return newSelected;
            });
        },
        [waitingListTickets]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            if (checked) {
                setAllSelected(true);
                let waitingListsTmp = {};
                waitingListTickets.forEach(waitingListTicket => {
                    waitingListsTmp[waitingListTicket.id] = waitingListTicket;
                });
                setSelectedLists(waitingListsTmp);
            } else {
                setAllSelected(false);
                setSelectedLists({});
            }
        },
        [waitingListTickets]
    );

    const onClose = React.useCallback(() => {
        setSubscriptionsToCancel([]);
        setIsSubmitting(false);
    }, []);

    const _filteredSubscriptions = React.useMemo(() => {
        return Object.values(selectedLists).length
            ? Object.values(selectedLists).filter(subscription => !['canceled', 'ongoing', 'used'].includes(subscription.status))
            : subscriptionsToCancel;
    }, [selectedLists, subscriptionsToCancel]);

    const cancelSubscriptions = React.useCallback(() => {
        setIsSubmitting(true);
        WaitingListTicketModel.perform_cancel_subscription(_filteredSubscriptions, organization.id, event.id, waitingListId, _filteredSubscriptions.length > 1 ? true : false)
            .then(response => {
                if (_filteredSubscriptions.length > 1) {
                    addNotification(
                        new Notification(
                            'async_actions',
                            'cancel_subscriptions',
                            'waiting_list_tickets',
                            response?.response?.task_id,
                            'ticket.waiting_list.subscribers.list.cancel_subscription.'
                        )
                    );
                    toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
                } else {
                    toast.success(t('ticket.waiting_list.subscribers.list.cancel_subscription.success', { count: _filteredSubscriptions.length }));
                }
                syncWaitingListTickets();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.waiting_list.subscribers.list.cancel_subscription.error'));
            })
            .finally(() => {
                setIsSubmitting(false);
                onClose();
            });
    }, [_filteredSubscriptions, organization, event, waitingListId, t, onClose, syncWaitingListTickets]);

    return (
        <>
            <ConfirmModal
                onClose={onClose}
                onAccept={cancelSubscriptions}
                isSubmitting={isSubmitting}
                objectToSave={Boolean(subscriptionsToCancel.length)}
                labelTitle={t('ticket.waiting_list.subscribers.list.cancel_subscription.title')}
                labelBody={
                    <>
                        {Object.values(selectedLists).length > _filteredSubscriptions.length && (
                            <Message type="default" title={t('ticket.waiting_list.subscribers.list.cancel_subscription.message_title_filtered')}>
                                {t('ticket.waiting_list.subscribers.list.cancel_subscription.message_body_filtered')}
                            </Message>
                        )}
                        <div>{t('ticket.waiting_list.subscribers.list.cancel_subscription.body', { count: _filteredSubscriptions.length })}</div>
                    </>
                }
            />
            {(subscriptionModal.open || subscriptionModal.waitingListTicket) && (
                <SubscriptionModal
                    waitingListTicket={subscriptionModal.waitingListTicket}
                    organization={organization}
                    event={event}
                    onClose={() => setSubscriptionModal({ open: false, waitingListTicket: null })}
                    open={subscriptionModal.open || subscriptionModal.waitingListTicket}
                    waitingList={waitingList}
                    syncWaitingListTickets={syncWaitingListTickets}
                />
            )}
            <WeezHeader
                title={loadingWaitingList ? <Loader size="small" inline active /> : waitingList?.name}
                backLink={{
                    onClick: () => {
                        setFilters({});
                        history.push(`/ticket/O${organization.pk}/E${event.id}/waiting_lists`);
                    }
                }}
                rightComponent={[
                    <Button key={0} onClick={() => setSubscriptionModal({ open: true, waitingListTicket: null })} primary icon={<Icon name="plus" />}>
                        {t('ticket.waiting_list.subscribers.list.create_new')}
                    </Button>,
                    <ButtonDropdown
                        key="dropdown"
                        size="large"
                        className={Style['dropdown-button-margin-left']}
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <Dropdown.Item
                                key="0"
                                item={
                                    <div className={Style['button-dropdown-item']}>
                                        <div>{t('ticket.waiting_list.subscribers.list.edit_settings')}</div>
                                        <Icon name="external-link" />
                                    </div>
                                }
                                onClick={() => {
                                    history.push(`/ticket/O${organization.id}/waiting_lists/${waitingListId}`);
                                }}
                            />
                        ]}
                    />
                ]}
            />
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={countSubscribers}
                        labelTotal={t('ticket.waiting_list.subscribers.list.total', { count: countSubscribers })}
                        labelReset={t('ticket.common.action.reset')}
                        defaultValues={initialFilters}
                        search={{
                            onChange: (synth, value) => {
                                onChangeSearch(value);
                                resetSelected();
                            },
                            handleReset: (synth, value) => {
                                onChangeSearch(null);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            }
                            setFilters(values);
                            resetSelected();
                        }}
                    >
                        <Filter
                            name="status_in"
                            multiple
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.waiting_list.subscribers.list.tableHeader.status')}
                            options={_statusOptions}
                            index="1"
                            labelCTA={t('ticket.common.action.apply')}
                        />
                    </FilterSetController>
                    <div>
                        <div className={Style['global-pagesize-container']}>
                            <SelectButton
                                tooltipTitle={t('ticket.waiting_list.noActionTooltip')}
                                title={
                                    Object.values(selectedLists).length > 0
                                        ? t('ticket.waiting_list.listsAction', { count: Object.values(selectedLists).length })
                                        : t('ticket.common.action.bulk')
                                }
                                objects={Object.values(selectedLists)}
                            >
                                <Dropdown.Item
                                    disabled={Object.values(selectedLists).length < 1}
                                    onClick={() => setSubscriptionsToCancel(Object.values(selectedLists).map(list => list))}
                                    deleteItem
                                >
                                    {t('ticket.waiting_list.subscribers.list.cancel_subscription.title')}
                                </Dropdown.Item>
                            </SelectButton>
                            <DropdownPageSize
                                loading={false}
                                current={pageSize}
                                onChange={setPageSize}
                                pageSizes={[10, 25, 50]}
                                onChangeCurrentpage={setCurrentPage}
                                totalCount={countSubscribers || 0}
                            />
                        </div>
                    </div>
                    <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })}>
                        <Table.THead>
                            <HeaderRow active={allSelected} ordering={ordering} waitingListOrdering={waitingList?.ordering_mode} onChangeSort={onChangeSort} />
                        </Table.THead>
                        <Table.TBody>
                            {waitingListTickets?.map((subscriber, index) => (
                                <SubscriberRow
                                    key={index}
                                    waitingListOrdering={waitingList?.ordering_mode}
                                    subscriber={subscriber}
                                    isChecked={Boolean(selectedLists[subscriber.id])}
                                    onSelected={handleElementsList}
                                    setSubscriptionsToCancel={setSubscriptionsToCancel}
                                    setSubscriptionModal={setSubscriptionModal}
                                />
                            ))}
                        </Table.TBody>
                    </Table>
                    <LoadingTable loading={false} />
                    {!loadingWaitingListTickets && !waitingListTickets?.length && (
                        <Card.EmptyState
                            title={t('ticket.waiting_list.subscribers.list.empty.title')}
                            subtitle={t('ticket.waiting_list.subscribers.list.empty.description')}
                            icon={<Icon name="list" />}
                        />
                    )}
                    <div style={{ textAlign: 'center', margin: '10px 0' }}>
                        <Paginator
                            currentPage={currentPage}
                            onPageChange={numberPage => {
                                setCurrentPage(numberPage);
                                resetSelected();
                            }}
                            totalCount={countSubscribers || 0}
                            pageSize={pageSize}
                        />
                    </div>
                </ContentCard>
            </Card>
        </>
    );
}
